export interface SecurityEvent {
  id: string;
  type: 'login' | 'failed_login' | 'unknown_device' | 'password_change' | 'account_blocked' | 'admin_access' | 'critical_event' | 'suspicious_activity' | 'ip_blocked' | 'session_terminated' | 'ip_whitelisted' | 'rate_limit_hit' | 'two_factor_failure' | 'config_change' | 'country_restricted';
  userId: string;
  userName: string;
  userRole: string;
  radioId: string;
  radioName: string;
  ipAddress: string;
  country: string;
  city: string;
  device: string;
  browser: string;
  os: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  details: string;
  auditLogId: string;
  resolved: boolean;
  resolvedBy: string;
  resolvedAt: string;
}

export interface BlockedAccount {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  reason: string;
  blockedAt: string;
  blockedBy: string;
  expiresAt: string;
}

export interface BlockedIP {
  id: string;
  ipAddress: string;
  reason: string;
  blockedAt: string;
  blockedBy: string;
  expiresAt: string;
  hitCount: number;
  country: string;
}

export interface DeviceSession {
  id: string;
  userId: string;
  userName: string;
  device: string;
  browser: string;
  os: string;
  ipAddress: string;
  country: string;
  startedAt: string;
  lastActive: string;
  isCurrent: boolean;
  isTrusted: boolean;
}

export interface SecurityAlert {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  timestamp: string;
  acknowledged: boolean;
  source: string;
  relatedEventId: string;
}

export interface SecurityConfig {
  sessionDurationMinutes: number;
  maxConcurrentSessions: number;
  maxFailedLoginAttempts: number;
  lockoutDurationMinutes: number;
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireSymbols: boolean;
  requireEmailVerification: boolean;
  requirePhoneVerification: boolean;
  twoFactorEnabled: boolean;
  twoFactorRequired: boolean;
  countryAccessMode: 'allow_all' | 'block_list' | 'allow_list';
  restrictedCountries: string[];
  allowedCountries: string[];
  ipWhitelist: string[];
  ipBlacklist: string[];
  rateLimits: RateLimit[];
  sessionInactivityTimeoutMinutes: number;
  forcePasswordResetDays: number;
  rememberMeEnabled: boolean;
}

export interface RateLimit {
  module: string;
  endpoint: string;
  maxRequests: number;
  windowSeconds: number;
  enabled: boolean;
}

export const mockSecurityConfig: SecurityConfig = {
  sessionDurationMinutes: 480,
  maxConcurrentSessions: 3,
  maxFailedLoginAttempts: 5,
  lockoutDurationMinutes: 30,
  passwordMinLength: 10,
  passwordRequireUppercase: true,
  passwordRequireNumbers: true,
  passwordRequireSymbols: true,
  requireEmailVerification: true,
  requirePhoneVerification: false,
  twoFactorEnabled: true,
  twoFactorRequired: true,
  countryAccessMode: 'block_list',
  restrictedCountries: ['KP', 'IR', 'SY', 'CU', 'SD'],
  allowedCountries: [],
  ipWhitelist: ['190.210.45.12', '200.115.88.34', '181.47.92.10'],
  ipBlacklist: ['45.33.32.156', '104.248.50.87', '178.128.200.45', '192.241.220.31', '45.55.36.77'],
  rateLimits: [
    { module: 'auth', endpoint: '/api/auth/login', maxRequests: 10, windowSeconds: 60, enabled: true },
    { module: 'auth', endpoint: '/api/auth/register', maxRequests: 3, windowSeconds: 3600, enabled: true },
    { module: 'auth', endpoint: '/api/auth/reset-password', maxRequests: 2, windowSeconds: 3600, enabled: true },
    { module: 'api', endpoint: '/api/v1/search', maxRequests: 100, windowSeconds: 60, enabled: true },
    { module: 'api', endpoint: '/api/v1/radios', maxRequests: 300, windowSeconds: 60, enabled: true },
    { module: 'api', endpoint: '/api/v1/upload', maxRequests: 20, windowSeconds: 60, enabled: true },
    { module: 'chat', endpoint: '/ws/chat/message', maxRequests: 60, windowSeconds: 60, enabled: true },
    { module: 'streaming', endpoint: '/api/stream/start', maxRequests: 5, windowSeconds: 60, enabled: true },
    { module: 'payments', endpoint: '/api/payments/process', maxRequests: 5, windowSeconds: 60, enabled: true },
    { module: 'admin', endpoint: '/api/admin/*', maxRequests: 200, windowSeconds: 60, enabled: true },
    { module: 'super_admin', endpoint: '/api/sa/*', maxRequests: 500, windowSeconds: 60, enabled: true },
  ],
  sessionInactivityTimeoutMinutes: 60,
  forcePasswordResetDays: 90,
  rememberMeEnabled: false,
};

export const mockSecurityEvents: SecurityEvent[] = [
  {
    id: 'sec-001',
    type: 'login',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'MacBook Pro M3',
    browser: 'Chrome 131',
    os: 'macOS 15.1',
    riskLevel: 'low',
    timestamp: '2026-07-12 07:15:22',
    details: 'Inicio de sesión exitoso con 2FA verificado desde IP confiable.',
    auditLogId: 'audit-9842',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-002',
    type: 'failed_login',
    userId: 'u-loc-07',
    userName: 'Martín Silva',
    userRole: 'Locutor',
    radioId: 'r-004',
    radioName: 'FM Éxito 98.3',
    ipAddress: '201.254.33.87',
    country: 'Colombia',
    city: 'Bogotá',
    device: 'Windows PC',
    browser: 'Firefox 128',
    os: 'Windows 11',
    riskLevel: 'medium',
    timestamp: '2026-07-12 07:12:45',
    details: '5 intentos fallidos consecutivos. Cuenta bloqueada temporalmente por 30 minutos.',
    auditLogId: 'audit-9841',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-003',
    type: 'unknown_device',
    userId: 'u-mod-03',
    userName: 'Lucía Fernández',
    userRole: 'Moderador',
    radioId: 'r-002',
    radioName: 'Radio Primavera 101.5',
    ipAddress: '88.24.150.33',
    country: 'España',
    city: 'Madrid',
    device: 'Samsung Galaxy S24',
    browser: 'Chrome Mobile 130',
    os: 'Android 14',
    riskLevel: 'high',
    timestamp: '2026-07-12 06:58:10',
    details: 'Acceso detectado desde dispositivo no reconocido y ubicación inusual. Se envió alerta por correo.',
    auditLogId: 'audit-9838',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-004',
    type: 'password_change',
    userId: 'u-radio-01',
    userName: 'Alejandro Ruiz',
    userRole: 'Radio Owner',
    radioId: 'r-001',
    radioName: 'Radio Huayra 104.3',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'MacBook Air M2',
    browser: 'Safari 18',
    os: 'macOS 15.1',
    riskLevel: 'low',
    timestamp: '2026-07-12 06:30:05',
    details: 'Cambio de contraseña exitoso. Se invalidaron todas las sesiones activas anteriores.',
    auditLogId: 'audit-9835',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-005',
    type: 'account_blocked',
    userId: 'u-anun-05',
    userName: 'Roberto Castillo',
    userRole: 'Anunciante',
    radioId: '—',
    radioName: '—',
    ipAddress: '185.220.101.34',
    country: 'Alemania',
    city: 'Frankfurt',
    device: 'Unknown',
    browser: 'Unknown',
    os: 'Unknown',
    riskLevel: 'critical',
    timestamp: '2026-07-12 05:45:33',
    details: 'Cuenta bloqueada automáticamente por actividad sospechosa. IP coincide con Tor exit node conocido. Se requiere revisión manual.',
    auditLogId: 'audit-9831',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-006',
    type: 'admin_access',
    userId: 'u-admin-02',
    userName: 'Valentina Ríos',
    userRole: 'Platform Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '200.115.88.34',
    country: 'Chile',
    city: 'Santiago',
    device: 'Dell XPS 15',
    browser: 'Edge 130',
    os: 'Windows 11',
    riskLevel: 'low',
    timestamp: '2026-07-12 05:20:18',
    details: 'Acceso administrativo a Platform Engine. Navegación a configuración global de seguridad.',
    auditLogId: 'audit-9828',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-007',
    type: 'critical_event',
    userId: 'system',
    userName: 'Sistema',
    userRole: 'System',
    radioId: '—',
    radioName: '—',
    ipAddress: '10.0.1.25',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Server',
    browser: '—',
    os: 'Ubuntu 24.04',
    riskLevel: 'critical',
    timestamp: '2026-07-12 04:55:00',
    details: 'Rate limit excedido en /api/auth/login desde múltiples IPs. Posible ataque de fuerza bruta distribuido. Se activaron contramedidas automáticas.',
    auditLogId: 'audit-9825',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-008',
    type: 'suspicious_activity',
    userId: 'u-oyen-12',
    userName: 'Pedro Gutiérrez',
    userRole: 'Oyente',
    radioId: 'r-008',
    radioName: 'La Voz del Pueblo 92.1',
    ipAddress: '45.33.32.156',
    country: 'United States',
    city: 'San Francisco',
    device: 'iPhone 15 Pro',
    browser: 'Safari Mobile 18',
    os: 'iOS 18.3',
    riskLevel: 'high',
    timestamp: '2026-07-12 04:30:42',
    details: 'IP en lista negra detectada. Múltiples intentos de acceso a endpoints administrativos con credenciales de oyente.',
    auditLogId: 'audit-9822',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-009',
    type: 'ip_blocked',
    userId: 'system',
    userName: 'Sistema',
    userRole: 'System',
    radioId: '—',
    radioName: '—',
    ipAddress: '104.248.50.87',
    country: 'United States',
    city: 'New York',
    device: '—',
    browser: '—',
    os: '—',
    riskLevel: 'medium',
    timestamp: '2026-07-12 04:15:10',
    details: 'IP bloqueada automáticamente tras 50 intentos de inyección SQL detectados por WAF.',
    auditLogId: 'audit-9818',
    resolved: true,
    resolvedBy: 'Sistema',
    resolvedAt: '2026-07-12 04:15:10',
  },
  {
    id: 'sec-010',
    type: 'session_terminated',
    userId: 'u-radio-03',
    userName: 'Gabriela Morales',
    userRole: 'Radio Admin',
    radioId: 'r-003',
    radioName: 'Estación Central 107.9',
    ipAddress: '181.47.92.10',
    country: 'Argentina',
    city: 'Rosario',
    device: 'HP EliteBook',
    browser: 'Chrome 131',
    os: 'Windows 10',
    riskLevel: 'low',
    timestamp: '2026-07-12 03:45:28',
    details: 'Sesión cerrada por inactividad tras 60 minutos. Política de timeout automático aplicada.',
    auditLogId: 'audit-9815',
    resolved: true,
    resolvedBy: 'Sistema',
    resolvedAt: '2026-07-12 03:45:28',
  },
  {
    id: 'sec-011',
    type: 'login',
    userId: 'u-amb-04',
    userName: 'Sofía Martínez',
    userRole: 'Embajador',
    radioId: 'r-005',
    radioName: 'Radio Activa 95.7',
    ipAddress: '191.102.88.45',
    country: 'Colombia',
    city: 'Medellín',
    device: 'Asus ZenBook',
    browser: 'Chrome 131',
    os: 'Windows 11',
    riskLevel: 'low',
    timestamp: '2026-07-12 03:20:15',
    details: 'Inicio de sesión exitoso desde dispositivo conocido. 2FA verificado.',
    auditLogId: 'audit-9811',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-012',
    type: 'two_factor_failure',
    userId: 'u-sop-02',
    userName: 'Diego Navarro',
    userRole: 'Soporte',
    radioId: '—',
    radioName: '—',
    ipAddress: '201.189.56.22',
    country: 'Chile',
    city: 'Valparaíso',
    device: 'Lenovo ThinkPad',
    browser: 'Firefox 128',
    os: 'Windows 11',
    riskLevel: 'medium',
    timestamp: '2026-07-12 02:55:40',
    details: 'Fallo en verificación 2FA. 3 intentos consecutivos con código incorrecto. Se notificó al usuario por email.',
    auditLogId: 'audit-9808',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-013',
    type: 'config_change',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'MacBook Pro M3',
    browser: 'Chrome 131',
    os: 'macOS 15.1',
    riskLevel: 'high',
    timestamp: '2026-07-12 02:30:00',
    details: 'Configuración de seguridad modificada: Se activó 2FA obligatorio para todos los administradores de radio.',
    auditLogId: 'audit-9805',
    resolved: true,
    resolvedBy: 'Carlos Mendoza',
    resolvedAt: '2026-07-12 02:30:00',
  },
  {
    id: 'sec-014',
    type: 'country_restricted',
    userId: 'u-oyen-45',
    userName: 'Anónimo',
    userRole: 'Oyente',
    radioId: '—',
    radioName: '—',
    ipAddress: '5.134.128.90',
    country: 'Iran',
    city: 'Tehran',
    device: 'Unknown',
    browser: 'Chrome 130',
    os: 'Windows 10',
    riskLevel: 'medium',
    timestamp: '2026-07-12 02:10:55',
    details: 'Acceso bloqueado por restricción geográfica. País en lista de bloqueo automático.',
    auditLogId: 'audit-9802',
    resolved: true,
    resolvedBy: 'Sistema',
    resolvedAt: '2026-07-12 02:10:55',
  },
  {
    id: 'sec-015',
    type: 'rate_limit_hit',
    userId: 'system',
    userName: 'Sistema',
    userRole: 'System',
    radioId: '—',
    radioName: '—',
    ipAddress: '178.128.200.45',
    country: 'Netherlands',
    city: 'Amsterdam',
    device: '—',
    browser: '—',
    os: '—',
    riskLevel: 'high',
    timestamp: '2026-07-12 01:45:30',
    details: 'Rate limit alcanzado en /api/admin/*. 200 solicitudes en 60 segundos desde IP única. IP agregada a lista negra temporal.',
    auditLogId: 'audit-9799',
    resolved: true,
    resolvedBy: 'Sistema',
    resolvedAt: '2026-07-12 01:45:30',
  },
  {
    id: 'sec-016',
    type: 'suspicious_activity',
    userId: 'u-sup-01',
    userName: 'Andrea Castillo',
    userRole: 'Personal Interno',
    radioId: '—',
    radioName: '—',
    ipAddress: '192.241.220.31',
    country: 'United States',
    city: 'New York',
    device: 'MacBook Pro M2',
    browser: 'Chrome 130',
    os: 'macOS 14.6',
    riskLevel: 'high',
    timestamp: '2026-07-12 01:20:18',
    details: 'Acceso desde IP de datacenter (DigitalOcean). Descarga masiva de datos de usuarios detectada. Investigación requerida.',
    auditLogId: 'audit-9796',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-017',
    type: 'login',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '200.115.88.34',
    country: 'Chile',
    city: 'Santiago',
    device: 'iPad Pro M4',
    browser: 'Safari Mobile 18',
    os: 'iPadOS 18.3',
    riskLevel: 'medium',
    timestamp: '2026-07-12 00:55:11',
    details: 'Inicio de sesión desde ubicación diferente a la habitual (Chile vs Argentina). 2FA verificado correctamente.',
    auditLogId: 'audit-9792',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-018',
    type: 'password_change',
    userId: 'u-oyen-88',
    userName: 'Mariana López',
    userRole: 'Oyente',
    radioId: '—',
    radioName: '—',
    ipAddress: '45.55.36.77',
    country: 'United States',
    city: 'Clifton',
    device: 'Android Tablet',
    browser: 'Chrome Mobile 129',
    os: 'Android 13',
    riskLevel: 'high',
    timestamp: '2026-07-12 00:30:45',
    details: 'Cambio de contraseña desde IP en lista negra. Cuenta posiblemente comprometida. Se forzó cierre de todas las sesiones.',
    auditLogId: 'audit-9788',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-019',
    type: 'admin_access',
    userId: 'u-admin-03',
    userName: 'Fernando Torres',
    userRole: 'Billing Manager',
    radioId: '—',
    radioName: '—',
    ipAddress: '181.47.92.10',
    country: 'Argentina',
    city: 'Rosario',
    device: 'Desktop PC',
    browser: 'Chrome 131',
    os: 'Windows 11',
    riskLevel: 'low',
    timestamp: '2026-07-11 23:45:30',
    details: 'Acceso al módulo de facturación. Revisión de pagos procesados del día.',
    auditLogId: 'audit-9785',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-020',
    type: 'critical_event',
    userId: 'system',
    userName: 'Sistema',
    userRole: 'System',
    radioId: '—',
    radioName: '—',
    ipAddress: '10.0.1.50',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Server',
    browser: '—',
    os: 'Ubuntu 24.04',
    riskLevel: 'critical',
    timestamp: '2026-07-11 23:15:00',
    details: 'Intento de acceso no autorizado al Identity Engine desde IP externa. Firewall bloqueó la conexión. Se generó alerta de seguridad nivel crítico.',
    auditLogId: 'audit-9782',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-021',
    type: 'login',
    userId: 'u-mod-06',
    userName: 'Javier Peña',
    userRole: 'Content Moderator',
    radioId: 'r-007',
    radioName: 'FM Cumbia 100.1',
    ipAddress: '181.120.67.34',
    country: 'Paraguay',
    city: 'Asunción',
    device: 'Acer Aspire',
    browser: 'Chrome 130',
    os: 'Windows 10',
    riskLevel: 'low',
    timestamp: '2026-07-11 22:30:42',
    details: 'Inicio de sesión exitoso. Acceso al panel de moderación de contenido.',
    auditLogId: 'audit-9778',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-022',
    type: 'failed_login',
    userId: 'u-radio-05',
    userName: 'Patricia Vega',
    userRole: 'Radio Admin',
    radioId: 'r-006',
    radioName: 'Radio Brisa 89.5',
    ipAddress: '186.52.90.12',
    country: 'Uruguay',
    city: 'Montevideo',
    device: 'Samsung Galaxy S23',
    browser: 'Chrome Mobile 130',
    os: 'Android 14',
    riskLevel: 'medium',
    timestamp: '2026-07-11 22:15:10',
    details: '4 intentos fallidos. 1 intento restante antes de bloqueo temporal.',
    auditLogId: 'audit-9774',
    resolved: false,
    resolvedBy: '',
    resolvedAt: '',
  },
  {
    id: 'sec-023',
    type: 'unknown_device',
    userId: 'u-oyen-156',
    userName: 'Tomás Herrera',
    userRole: 'Oyente',
    radioId: 'r-002',
    radioName: 'Radio Primavera 101.5',
    ipAddress: '79.180.45.22',
    country: 'Israel',
    city: 'Tel Aviv',
    device: 'Huawei P60',
    browser: 'Chrome Mobile 129',
    os: 'HarmonyOS 4',
    riskLevel: 'medium',
    timestamp: '2026-07-11 21:50:33',
    details: 'Nuevo dispositivo detectado desde país no habitual para este usuario. Verificación por email enviada.',
    auditLogId: 'audit-9770',
    resolved: true,
    resolvedBy: '—',
    resolvedAt: '—',
  },
  {
    id: 'sec-024',
    type: 'session_terminated',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'MacBook Pro M3',
    browser: 'Chrome 131',
    os: 'macOS 15.1',
    riskLevel: 'low',
    timestamp: '2026-07-11 21:30:00',
    details: 'Todas las sesiones activas cerradas manualmente por el usuario. Motivo: cambio de dispositivo.',
    auditLogId: 'audit-9766',
    resolved: true,
    resolvedBy: 'Carlos Mendoza',
    resolvedAt: '2026-07-11 21:30:00',
  },
  {
    id: 'sec-025',
    type: 'ip_whitelisted',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    radioId: '—',
    radioName: '—',
    ipAddress: '181.47.92.10',
    country: 'Argentina',
    city: 'Rosario',
    device: '—',
    browser: '—',
    os: '—',
    riskLevel: 'low',
    timestamp: '2026-07-11 21:00:15',
    details: 'IP 181.47.92.10 agregada a lista blanca. Acceso sin restricciones para sede administrativa Rosario.',
    auditLogId: 'audit-9762',
    resolved: true,
    resolvedBy: 'Carlos Mendoza',
    resolvedAt: '2026-07-11 21:00:15',
  },
];

export const mockBlockedAccounts: BlockedAccount[] = [
  {
    id: 'ba-001',
    userId: 'u-anun-05',
    userName: 'Roberto Castillo',
    userEmail: 'roberto.castillo@email.com',
    reason: 'Actividad sospechosa - IP de Tor exit node',
    blockedAt: '2026-07-12 05:45:33',
    blockedBy: 'Sistema',
    expiresAt: '—',
  },
  {
    id: 'ba-002',
    userId: 'u-oyen-212',
    userName: 'Nicolás Duran',
    userEmail: 'nicolas.duran@email.com',
    reason: 'Múltiples violaciones de términos de uso',
    blockedAt: '2026-07-11 18:30:00',
    blockedBy: 'Valentina Ríos',
    expiresAt: '2026-07-18 18:30:00',
  },
  {
    id: 'ba-003',
    userId: 'u-loc-07',
    userName: 'Martín Silva',
    userEmail: 'martin.silva@email.com',
    reason: 'Bloqueo temporal por intentos fallidos de login',
    blockedAt: '2026-07-12 07:12:45',
    blockedBy: 'Sistema',
    expiresAt: '2026-07-12 07:42:45',
  },
];

export const mockBlockedIPs: BlockedIP[] = [
  {
    id: 'bip-001',
    ipAddress: '45.33.32.156',
    reason: 'Intento de fuerza bruta detectado',
    blockedAt: '2026-07-12 04:30:42',
    blockedBy: 'Sistema',
    expiresAt: '2026-07-13 04:30:42',
    hitCount: 234,
    country: 'United States',
  },
  {
    id: 'bip-002',
    ipAddress: '104.248.50.87',
    reason: 'Inyección SQL detectada por WAF',
    blockedAt: '2026-07-12 04:15:10',
    blockedBy: 'Sistema',
    expiresAt: '2026-07-12 10:15:10',
    hitCount: 50,
    country: 'United States',
  },
  {
    id: 'bip-003',
    ipAddress: '178.128.200.45',
    reason: 'Rate limit excedido en /api/admin/*',
    blockedAt: '2026-07-12 01:45:30',
    blockedBy: 'Sistema',
    expiresAt: '2026-07-12 03:45:30',
    hitCount: 200,
    country: 'Netherlands',
  },
  {
    id: 'bip-004',
    ipAddress: '192.241.220.31',
    reason: 'Descarga masiva de datos detectada',
    blockedAt: '2026-07-12 01:20:18',
    blockedBy: 'Carlos Mendoza',
    expiresAt: '—',
    hitCount: 89,
    country: 'United States',
  },
  {
    id: 'bip-005',
    ipAddress: '45.55.36.77',
    reason: 'Actividad maliciosa recurrente',
    blockedAt: '2026-07-12 00:30:45',
    blockedBy: 'Sistema',
    expiresAt: '—',
    hitCount: 412,
    country: 'United States',
  },
];

export const mockDeviceSessions: DeviceSession[] = [
  {
    id: 'ds-001',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    device: 'MacBook Pro M3',
    browser: 'Chrome 131',
    os: 'macOS 15.1',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    startedAt: '2026-07-12 07:15:22',
    lastActive: '2026-07-12 07:45:00',
    isCurrent: true,
    isTrusted: true,
  },
  {
    id: 'ds-002',
    userId: 'u-admin-01',
    userName: 'Carlos Mendoza',
    device: 'iPad Pro M4',
    browser: 'Safari Mobile 18',
    os: 'iPadOS 18.3',
    ipAddress: '200.115.88.34',
    country: 'Chile',
    startedAt: '2026-07-12 00:55:11',
    lastActive: '2026-07-12 01:30:00',
    isCurrent: false,
    isTrusted: false,
  },
  {
    id: 'ds-003',
    userId: 'u-radio-01',
    userName: 'Alejandro Ruiz',
    device: 'MacBook Air M2',
    browser: 'Safari 18',
    os: 'macOS 15.1',
    ipAddress: '190.210.45.12',
    country: 'Argentina',
    startedAt: '2026-07-12 06:30:05',
    lastActive: '2026-07-12 07:30:00',
    isCurrent: true,
    isTrusted: true,
  },
  {
    id: 'ds-004',
    userId: 'u-mod-03',
    userName: 'Lucía Fernández',
    device: 'Samsung Galaxy S24',
    browser: 'Chrome Mobile 130',
    os: 'Android 14',
    ipAddress: '88.24.150.33',
    country: 'España',
    startedAt: '2026-07-12 06:58:10',
    lastActive: '2026-07-12 06:58:10',
    isCurrent: true,
    isTrusted: false,
  },
  {
    id: 'ds-005',
    userId: 'u-amb-04',
    userName: 'Sofía Martínez',
    device: 'Asus ZenBook',
    browser: 'Chrome 131',
    os: 'Windows 11',
    ipAddress: '191.102.88.45',
    country: 'Colombia',
    startedAt: '2026-07-12 03:20:15',
    lastActive: '2026-07-12 07:40:00',
    isCurrent: true,
    isTrusted: true,
  },
  {
    id: 'ds-006',
    userId: 'u-sop-02',
    userName: 'Diego Navarro',
    device: 'Lenovo ThinkPad',
    browser: 'Firefox 128',
    os: 'Windows 11',
    ipAddress: '201.189.56.22',
    country: 'Chile',
    startedAt: '2026-07-12 02:55:40',
    lastActive: '2026-07-12 05:20:00',
    isCurrent: false,
    isTrusted: true,
  },
  {
    id: 'ds-007',
    userId: 'u-sup-01',
    userName: 'Andrea Castillo',
    device: 'MacBook Pro M2',
    browser: 'Chrome 130',
    os: 'macOS 14.6',
    ipAddress: '192.241.220.31',
    country: 'United States',
    startedAt: '2026-07-12 01:20:18',
    lastActive: '2026-07-12 01:20:18',
    isCurrent: true,
    isTrusted: false,
  },
];

export const mockSecurityAlerts: SecurityAlert[] = [
  {
    id: 'alert-001',
    title: 'Posible ataque de fuerza bruta distribuido',
    description: 'Múltiples IPs externas han excedido el rate limit en /api/auth/login. Se activaron contramedidas automáticas pero se requiere revisión manual.',
    severity: 'critical',
    category: 'auth',
    timestamp: '2026-07-12 04:55:00',
    acknowledged: false,
    source: 'Rate Limiter',
    relatedEventId: 'sec-007',
  },
  {
    id: 'alert-002',
    title: 'Intento de acceso no autorizado al Identity Engine',
    description: 'Conexión bloqueada por firewall desde IP externa hacia el Identity Engine. Se requiere verificar que no haya habido acceso previo.',
    severity: 'critical',
    category: 'identity',
    timestamp: '2026-07-11 23:15:00',
    acknowledged: false,
    source: 'Firewall',
    relatedEventId: 'sec-020',
  },
  {
    id: 'alert-003',
    title: 'Cuenta posiblemente comprometida',
    description: 'Cambio de contraseña detectado desde IP en lista negra para usuario Mariana López. Se forzó cierre de sesiones.',
    severity: 'high',
    category: 'account',
    timestamp: '2026-07-12 00:30:45',
    acknowledged: false,
    source: 'Account Monitor',
    relatedEventId: 'sec-018',
  },
  {
    id: 'alert-004',
    title: 'Descarga masiva de datos sospechosa',
    description: 'Usuario Andrea Castillo descargó un volumen inusualmente alto de datos de usuarios desde IP de datacenter.',
    severity: 'high',
    category: 'data',
    timestamp: '2026-07-12 01:20:18',
    acknowledged: false,
    source: 'Data Monitor',
    relatedEventId: 'sec-016',
  },
  {
    id: 'alert-005',
    title: 'Bloqueo de IPs por WAF activado',
    description: 'El WAF bloqueó y agregó a lista negra 3 IPs en las últimas 12 horas por intentos de inyección SQL y XSS.',
    severity: 'medium',
    category: 'waf',
    timestamp: '2026-07-12 07:00:00',
    acknowledged: true,
    source: 'WAF',
    relatedEventId: 'sec-009',
  },
];